import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Heading from 'components/atoms/Heading';
import { IconButton } from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import Link from 'components/atoms/Link';
import Text from 'components/atoms/Text';
import Carousel from 'components/organisms/Carousel';
import NativePlayer from 'components/organisms/NativePlayer';
import useDeviceQueries from 'hooks/useDeviceQueries';
import useWindowScroll from 'hooks/useWindowScroll';
import { baseURL } from 'utils/functions';

export interface VideoTypes {
  videoMobile?: string;
  videoTablet?: string;
  videoDesktop?: string;
}
interface ImgType extends VideoTypes {
  imageDesktop: string;
  imageMobile?: string;
  imageTablet?: string;
  title: string;
  description: string;
  link?: string;
  video?: boolean;
}

interface BannerProps {
  handleScrollDown?: () => void;
  data?: ImgType[];
}

interface VideoWithDeviceProps extends VideoTypes {
  isPlay?: boolean;
  loop?: boolean;
  onEnded?: () => void;
}

export const VideoWithDevice:React.FC<VideoWithDeviceProps> = ({
  isPlay,
  videoMobile,
  videoTablet,
  videoDesktop,
  loop,
  onEnded,
}) => {
  const { isMobile, isTablet } = useDeviceQueries();
  const [accept, setAccept] = useState(true);
  const [muted, setMuted] = useState(true);

  const sourceVideo = useMemo(() => {
    if (isMobile) {
      return videoMobile || videoDesktop;
    }
    if (isTablet) {
      return videoTablet || videoDesktop;
    }
    return videoDesktop;
  }, [isMobile, isTablet, videoDesktop, videoMobile, videoTablet]);

  const refBannerHome = useRef<HTMLDivElement | null>(null);

  useWindowScroll(() => {
    if (!refBannerHome.current) return;
    if (window.scrollY > (refBannerHome.current.clientHeight)) {
      setAccept(false);
    } else {
      setAccept(true);
    }
  });

  return (
    <div className="o-banner_video_wrap" ref={refBannerHome}>
      <NativePlayer
        src={sourceVideo?.includes('http') ? sourceVideo : baseURL(sourceVideo)}
        playing={isPlay && accept}
        muted={muted}
        loop={loop}
        onEnded={onEnded}
        isYoutube={sourceVideo?.includes('http')}
        showYoutube={isPlay}
      />
      {!sourceVideo?.includes('http') && (
      <div className="o-banner_video_controls">
        <div className="o-banner_video_play">
          <IconButton handleClick={() => setAccept(!accept)} iconName={accept ? 'pause' : 'play'} iconSize={{ width: 24, height: 24 }} buttonSize="30" bgColor="transparent" />
        </div>
        <div className="o-banner_video_volume">
          <IconButton handleClick={() => setMuted(!muted)} iconName={muted ? 'volumeMute' : 'volume'} iconSize={{ width: 24, height: 24 }} buttonSize="30" bgColor="transparent" />
        </div>
      </div>
      )}
    </div>
  );
};

const Banner: React.FC<BannerProps> = ({
  handleScrollDown,
  data,
}) => {
  const { t } = useTranslation();
  const refInterval = useRef<NodeJS.Timeout>();
  const [mySwiper, setMySwiper] = useState<any>();
  const [indexActive, setIndexActive] = useState(0);

  const onEnded = useCallback(() => {
    if (!data || data.length <= 1 || !mySwiper) return;
    if (mySwiper.slideNext && indexActive < data.length - 1) {
      mySwiper.slideNext();
    }
    if (mySwiper.slideTo && indexActive === data.length - 1) {
      mySwiper.slideTo(0);
    }
  }, [data, indexActive, mySwiper]);

  useEffect(() => {
    if ((!data || data.length <= 1 || !mySwiper) || data.length - 1 < indexActive) return;
    const find = data[indexActive];
    if (find.video) return;
    if (refInterval.current) {
      clearTimeout(refInterval.current);
    }
    refInterval.current = setTimeout(() => {
      onEnded();
    }, 5000);

    // eslint-disable-next-line consistent-return
    return () => refInterval.current && clearTimeout(refInterval.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, indexActive, mySwiper]);

  return (
    <div className="o-banner">
      {data && data.length > 0 && (
      <>
        <Carousel
          id={123}
          centerInsufficientSlides
          slidesPerView={1}
          spaceBetween={0}
          positionNavigation="useBanner"
          variantNavigation="white"
          effect="fade"
          onSlideChange={(swiper) => {
            setIndexActive(swiper.activeIndex);
          }}
          onInit={(ev) => {
            setMySwiper(ev);
          }}
        >
          {data.map((item, idx) => (
            <React.Fragment key={`banner-home-${idx.toString()}`}>
              {item.video
                ? (
                  <div className="o-banner_video">
                    <VideoWithDevice
                      {...item}
                      isPlay={idx === indexActive}
                      onEnded={onEnded}
                      loop={data.length === 1}
                    />
                  </div>
                ) : (
                  <Link
                    href={item.link || ''}
                  >
                    <div className="o-banner_image">
                      <Image
                        alt={`slider-${idx.toString()}`}
                        srcMobile={item.imageMobile}
                        srcTablet={item.imageTablet}
                        imgSrc={item.imageDesktop}
                        ratio="16x9"
                        size="cover"
                      />
                      {(item.title || item.description) && (
                      <div className={`o-banner_tag ${idx === indexActive ? 'active' : ''}`}>
                        <div className="o-banner_tag_content">
                          <div className="o-banner_tag_title">
                            <Heading type="h3" modifiers={['700', 'white', 'uppercase']} content={item.title} />
                          </div>
                          <div className="o-banner_tag_description">
                            <Text type="div" modifiers={['400', 'white']} content={item.description} />
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  </Link>
                )}
            </React.Fragment>
          ))}
        </Carousel>
        <div className="o-banner_mouse" onClick={handleScrollDown}>
          <Text modifiers={['center', '12x16', '500', 'white']}>
            {t('page_home.scroll_down')}
          </Text>
          <div className="o-banner_mouse_icon" />
        </div>
      </>
      )}
    </div>
  );
};

Banner.defaultProps = {
  handleScrollDown: undefined,
  data: undefined,
};

VideoWithDevice.defaultProps = {
  isPlay: undefined,
  onEnded: undefined,
  loop: undefined,
};

export default Banner;
